import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { AnimationOptions } from 'ngx-lottie';

import { NavigationEnd, Router } from '@angular/router';
import * as fromToDoSelectors from '../app/features/todos/selectors';
import { ScreenName, SessionStorageIds } from './shared/enums';
import { ILegalEntitiesBusinessesResponse } from './features/todos/models/businesses.model';
import { Store } from '@ngrx/store';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { LoaderService } from './core/services/loader-service.service';
import { IpServiceService } from './core/services/ip-service.service';
import { APP_CONFIG } from '../app/config/app-config.module';
import { AppConfig } from '../app/core/config-models/app-config.model';
import { catchError, map } from 'rxjs/operators';
import { IBusinessUserRolesResponse } from './features/auth/models/business-user-roles.model';
import * as authSelector from '../app/features/auth/selectors';
import { SharedService } from './shared/shared.service';
import { AnimationItem } from 'lottie-web';
import { ThemeService } from './shared/theme.service';

/** One of the signed-in user's past conversations, as returned by HayDesk.history(). */
interface HayDeskConversationSummary {
  id: string;
  title: string;
  status: 'Open' | 'InProgress' | 'Resolved' | 'Closed';
  lastMessagePreview?: string;
  lastMessageAt?: string;
  createdAt?: string;
}

/** Minimal surface of the HayDesk widget SDK that this app uses (see widget.js). */
interface HayDeskSdk {
  identify: (user: { name?: string; email?: string; phone?: string } | null) => void;
  /** Set/clear the signed-in user's stable id after boot (pass null on logout). */
  setExternalReference: (ref: string | null) => void;
  history: () => Promise<HayDeskConversationSummary[]>;
  openConversation: (conversationId: string) => Promise<boolean>;
}

declare global {
  interface Window {
    HayDesk?: HayDeskSdk;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  authCheck?: boolean = false;
  screenName?: string;
  isSideNavEnabled: boolean;
  subscription: Subscription = new Subscription();
  rolesSubscription: Subscription = new Subscription();
  isLoading: Subject<boolean>;
  options: AnimationOptions = {
    path: '../assets/Lottie/hayrick-loader.json'
  };
  selectedLegalEntity: ILegalEntitiesBusinessesResponse;
  isWilliamStrikeUser: boolean = false;
  isToDoPage: boolean = false;
  isFromToDo: string | null;
  roleName?: string;
  roles$: Observable<IBusinessUserRolesResponse>;
  /** Display name of the logged-in user, passed to the HayDesk widget post-login. */
  private currentUserName?: string;
  /** Stable user id (business-user userId), pushed to HayDesk as externalReference so the
   *  signed-in user is recognised across devices / sessions (null when logged out). */
  private currentUserExternalRef?: string;

  constructor(
    private router: Router,
    private store: Store,
    public sharedService: SharedService,
    private loaderService: LoaderService,
    private ip: IpServiceService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private themeService: ThemeService
  ) {
    this.isLoading = this.loaderService.isLoading;
    this.subscription.add(
      this.store.select(fromToDoSelectors.getIsSideNavEnabled).subscribe(sideNavEnabled => (this.isSideNavEnabled = sideNavEnabled))
    );

    this.sideNavSubscription();

    // Keep the HayDesk chat identity in sync with the logged-in user: once the
    // business user role (and therefore the user's name) resolves, push it to the
    // widget so the pre-chat form is skipped post-login.
    this.subscription.add(
      this.store.select(authSelector.selectBusinessUserRole).subscribe(role => {
        this.currentUserName = role?.userName;
        this.currentUserExternalRef = role?.userId;
        this.syncHaydeskIdentity();
      })
    );

    // Apply styles as per the client url
    this.themeService.applyTheme(this.sharedService.getSubDomain());
  }

  /** True when the user has a session token (logged in). */
  private isLoggedIn(): boolean {
    return !!sessionStorage.getItem('token');
  }

  sideNavSubscription() {
    this.router.events.subscribe((evt: any) => {
      if (evt instanceof NavigationEnd) {
        // Re-sync chat identity on every navigation so logout (which clears the
        // session) reverts the widget to the anonymous pre-chat form.
        this.syncHaydeskIdentity();
        this.screenName = evt.url;
        this.isToDoPage = false;
        var url = evt.url.split('?')[0];
        // Keep isFromTODO / isTODO in sync so side nav shows correct menu (TO DO vs legal entity)
        this.isFromToDo = sessionStorage.getItem('IsFromToDo');
        this.sharedService.isFromTODO = url !== '/search' && url !== '/settings' && this.isFromToDo === 'true';
        this.sharedService.isTODO = url === '/todo';
        this.sharedService.isAdminDashboard = url === '/admin-dashboard';
        switch (url) {
          case '/': //auth
            this.authCheck = false;
            break;
          case '/auth':
            this.authCheck = false;
            break;
          case '/auth/otp':
            this.authCheck = false;
            break;
          case '/auth/passcode':
            this.authCheck = false;
            break;
          case '/auth/marketing-options':
            this.authCheck = false;
            break;
          case '/auth/forgot-passcode-otp':
            this.authCheck = false;
            break;
          case '/auth/mobile-submit':
            this.authCheck = false;
            break;
          case '/auth/forgot-passcode':
            this.authCheck = false;
            break;
          case '/auth/sign-up':
            this.authCheck = false;
            break;
          case '/verify-email':
            this.authCheck = false;
            this.hideSideNavBar();
            break;
          case '/auth/identification-check':
            this.authCheck = false;
            break;
          case '/todo':
            this.isToDoPage = true;
            this.sideNavBarControl();
            break;
          case '/admin-dashboard':
            this.isToDoPage = true;
            this.sideNavBarControl();
            break;
          case '/settings':
            this.hideSideNavBar();
            break;
          case '/search':
            this.hideSideNavBar();
            break;
          case '/payment-mandate/credit-card-mandate':
            this.hideSideNavBar();
            break;
          case '/payment-mandate/debit-order-mandate':
            this.hideSideNavBar();
            break;
          case '/employee/employee-details':
            this.hideSideNavBar();
            break;
          case '/product-solutions':
            this.sideNavBarControl();
            break;
          case '/product-detail':
            this.hideSideNavBar();
            break;
          case '/product-solutions/editSolution':
            this.sideNavBarControl();
            break;
          case '/product-solutions/addSolution':
            this.hideSideNavBar();
            break;
          case '/legal-entities':
            this.sideNavBarControl();
            break;
          case '/legal-entities/offerings':
            this.sideNavBarControl();
            break;
          case '/legal-entities/manage-products':
            this.sideNavBarControl();
            break;
          case '/products':
            this.sideNavBarControl();
            break;
          case '/manage-employees':
            this.sideNavBarControl();
            break;
          case '/manage-users':
            this.sideNavBarControl();
            break;
          case '/roles':
            this.sideNavBarControl();
            break;
          case '/legal-entity-setup':
            this.sideNavBarControl();
            break;
          case '/reports':
            this.hideSideNavBar();
            break;
          default:
            this.sideNavBarControl();
            break;
        }


        switch (true) {
          case evt.url.toString().includes('todo'):
            this.screenName = ScreenName.TODO;
            break;
          case evt.url.toString().includes('bulk on-board'):
            this.screenName = ScreenName.ON_BOARD;
            break;
          case evt.url.toString().includes('legal entities'):
            this.screenName = ScreenName.LEGAL_ENTITIES;
            break;
          case evt.url.toString().includes('search'):
            this.screenName = ScreenName.SEARCH;
            break;
          case evt.url.toString().includes('reports'):
            this.screenName = ScreenName.REPORTS;
            break;
        }
      }
    });
  }


  ngOnDestroy(): void {
    this.rolesSubscription.unsubscribe();
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.getIP();
    this.loadHaydeskWidget();
  }

  /**
   * Injects the HayDesk chat widget script using the current environment's config
   * (app-config.<env>.json -> APP_CONFIG). No-op if config or company key is missing,
   * and guarded so it only ever injects once. data-company-key makes the widget
   * auto-boot; the signed-in user's stable id is pushed afterwards via
   * setExternalReference (see syncHaydeskIdentity).
   */
  private loadHaydeskWidget(): void {
    const config = this.appConfig.haydesk;
    if (!config || !config.scriptUrl || !config.companyKey) {
      return;
    }
    if (document.getElementById('haydesk-widget')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'haydesk-widget';
    script.src = config.scriptUrl;
    script.async = true;
    script.setAttribute('data-company-key', config.companyKey);
    script.setAttribute('data-title', config.title || 'Chat with us');
    script.setAttribute('data-position', config.position || 'bottom-right');
    // Once the widget SDK is available, push the current login identity + external ref.
    script.onload = () => this.syncHaydeskIdentity();
    document.body.appendChild(script);
  }

  /**
   * Tells the HayDesk widget who the current visitor is.
   *
   * - Post-login: passes the user's name + mobile so the pre-chat form is skipped
   *   and they can ask their question straight away.
   * - Pre-login (or after logout): passes null so the widget falls back to the
   *   anonymous pre-chat form that collects the details itself.
   *
   * No-op until the widget script has loaded; it is re-invoked on load, on login
   * state changes and on navigation, so the identity converges either way.
   */
  private syncHaydeskIdentity(): void {
    const haydesk = window.HayDesk;
    if (!haydesk || typeof haydesk.identify !== 'function') {
      return;
    }
    // Push the stable user id so the signed-in user is recognised across devices and
    // their chat history binds to them; null on logout (both are no-ops when unchanged).
    if (typeof haydesk.setExternalReference === 'function') {
      haydesk.setExternalReference(this.isLoggedIn() ? (this.currentUserExternalRef || null) : null);
    }
    if (!this.isLoggedIn()) {
      haydesk.identify(null);
      return;
    }
    const phone = sessionStorage.getItem(SessionStorageIds.USER_MOBILE) || undefined;
    const name = this.currentUserName || undefined;
    // The widget treats identity as present only when email or phone is set; with
    // neither we leave it on the pre-chat form rather than passing a nameless user.
    if (!phone) {
      return;
    }
    haydesk.identify({ name, phone });
  }

  getIP() {
    this.ip.getIPAddress().pipe(
      map((data: any) => {
        if (data && data.ip) {
          sessionStorage.setItem('ipAddress', data.ip);
          return data.ip;
        } else {
          throw new Error('No IP address found in response');
        }
      }),
      catchError((error: any) => {
        console.error('Error fetching IP address', error);
        sessionStorage.setItem('ipAddress', '0.0.0.0');
        return of('0.0.0.0');
      })
    )
  }

  hideSideNavBar() {
    const pageElement = document.getElementById('page');
    if (pageElement) {
      pageElement.style.marginLeft = '0px';
    }
    this.authCheck = false;
  }

  sideNavBarControl() {
    this.resetSideNav();
    const pageElement = document.getElementById('page');
    if (this.isLoggedIn() && this.isSideNavEnabled && pageElement) {
      pageElement.style.marginLeft = '250px';
    }
    this.authCheck = this.isLoggedIn();
  }

  removeLegalEntityFromSession() {
    sessionStorage.removeItem('legalEntity');
  }

  resetSideNav() {
    const pageElement = document.getElementById('page');
    if (pageElement) {
      pageElement.style.marginLeft = '65px';
    }
  }

  animationCreated(animationItem: AnimationItem): void {
  }
}
